import React, { useRef, useState, useContext, useEffect, useCallback, memo } from 'react';
import * as ol from 'ol';
import MapContext from '../controller/MapProvider';
import Waiting from './Waiting';
import { addPropsToChildren } from '../controller/addPropsToChildren';
import { pointWithinDomElement } from '../controller/CollisionDetection';
import { fromLonLat, toLonLat } from 'ol/proj';

const fn = () => {};

const defaultLayerFilter = (lyr) => {
    if (lyr.get('opacity') === 0 || !lyr.get('visible')) return false; //ΔVΔVΔ: No invisible layers
    return lyr;
};

const Map = (props) => {
    const { verbose = false, children, id = 'mapContainer', zoom = 4, projection, minZoom = 2, maxZoom = 15, initViewExtent: initExtent, extent: maxExtent, center = [0, 0] } = props;
    const { onZoom = fn, layerFilter: layerFilterFn = defaultLayerFilter, onClickFeature = fn, onHoverFeature = fn, onCenter = fn, onInitMap = fn, onMove = fn, onLoadStatus = fn } = props;

    const mapRef = useRef();
    const { map, setZoomRead } = useContext(MapContext);
    const [mouseIsDown, setMouseIsDown] = useState(false);
    const [isWorking, setIsWorking] = useState(1);
    const [loadSet, setLoadSet] = useState(new Set());
    const loadQueue = useRef(new Set());

    const mouseDownAction = () => {
        setMouseIsDown(true);
    };
    const mouseUpAction = () => {
        setMouseIsDown(false);
    };
    const onmapclick = (e) => {
        console.log(e.coordinate);
        if (pixelCollidesWithUI(e.pixel)) return;
        const targetFeatures = map.getFeaturesAtPixel(e.pixel, {
            layerFilter: layerFilterFn
        });
        onClickFeature(targetFeatures[0]);
    };

    const mouseoutMap = () => {
        onHoverFeature(false);
        setMouseIsDown(false);
    };

    //pointWithinDomElement
    const pixelCollidesWithUI = (pixel, padding = 10) => {
        //check that map pixel is not hovering over map controls
        const controls = mapRef.current.querySelectorAll('.ol-overlaycontainer-stopevent .ol-control');
        for (var i = 1; i < controls.length; i++) {
            var control = controls[i];
            const controlCollisionDetected = pointWithinDomElement(pixel[0], pixel[1], control, padding, mapRef.current); //control && detectCollision(pixel[0], pixel[1], getRelativeBoundingClientRect(control), padding);
            if (controlCollisionDetected) return true;
        }
        const downloadButton = mapRef.current.querySelector('.ol-overlaycontainer-stopevent button.map-download');
        const downloadCollisionDetected = pointWithinDomElement(pixel[0], pixel[1], downloadButton, padding, mapRef.current); //downloadButton && detectCollision(pixel[0], pixel[1], getRelativeBoundingClientRect(downloadButton), padding);
        if (downloadCollisionDetected) return true;
        return false;
    };

    const mapPointerMove = (e) => {
        if (e.dragging || mouseIsDown) {
            onHoverFeature(false);
            return;
        }
        if (pixelCollidesWithUI(e.pixel)) return;
        const hoveredFeatures = map.getFeaturesAtPixel(e.pixel, {
            layerFilter: layerFilterFn
        });
        onHoverFeature(hoveredFeatures[0]);
    };

    const mapmoveend = useCallback(
        (m) => {
            if (!map) return;
            let mapObject = map;
            let e = map.getView().calculateExtent(mapObject.getSize());
            let c = map.getView().getCenter();
            let z = map.getView().getZoom();
            setZoomRead(z); //
            onZoom(z);
            onCenter(c);
            onMove({ zoom: z, center: c, extent: e, map }); //props function
        },
        [map]
    );

    const moveMapTo = (extent) => {
        if (!map) return;
        map.getView().fit(extent, map.getSize());
        mapmoveend();
    };
    const onChildLoadStatus = (id, status) => {
        if (status === 0) {
            loadQueue.current.add(id);
            setLoadSet(new Set(loadQueue.current));
        }
        if (status === 1) {
            loadQueue.current.delete(id);
            setLoadSet(new Set(loadQueue.current));
        }

        //ΔVΔVΔ: If for some reason the status passed is not 1 or 0 do nothing
    };

    const mappedChildren = addPropsToChildren(children, {
        prefix: 'map_util_',
        map,
        propsFilter: (index, child, props) => {
            props.onLoadStatus = (cid, status) => onChildLoadStatus(props.id + '_' + cid, status);
            return props;
        }
    });
    const initMap = () => {
        let options = {
            view: new ol.View({
                extent: maxExtent,
                projection,
                minZoom,
                maxZoom,
                showFullExtent: true
            }),
            layers: [],
            controls: [],
            overlays: []
        };
        let mapObject = new ol.Map(options);
        mapObject.setTarget(mapRef.current);

        mapObject.getView().fit(initExtent, mapObject.getSize());
        document.addEventListener('mousedown', mouseDownAction);
        document.addEventListener('mouseup', mouseUpAction);
        onInitMap(mapObject);
        var tst = [-14150703.25072198, 1724460.655959669, -7075872.59319234, 6815350.164313462];
        var proj = mapObject.getView().getProjection();
        console.log('tst', toLonLat(tst, proj));

        // var rtest = [-132.1875000, 48.0093777, -106.9921830, 47.9636190]
        var alas = [-19430660.90751849, 6002448.621339999, -13665405.635740003, 12265255.002859998];
        console.log('tst2', toLonLat(alas.slice(0, 2), proj), toLonLat(alas.slice(2), proj));
        return mapObject;
    };
    useEffect(() => {
        const mapObject = initMap();
        window.addEventListener('forceresize', () => {
            setTimeout(() => {
                mapObject.updateSize();
            }, [500]);
        });
        return () => {
            // ΔVΔVΔ: On component unmount
            mapObject.setTarget(undefined);
        };
    }, []);

    // useEffect(() => mapmoveend(), [tab]);// there was a time this was needed, probably when the content map tab was not ALWAYS active

    useEffect(() => {
        if (!map) return;
        map.addEventListener('moveend', mapmoveend);
        map.addEventListener('pointermove', mapPointerMove);
        map.addEventListener('click', onmapclick);
        map.getViewport().addEventListener('mouseout', mouseoutMap);

        return () => {
            map.removeEventListener('moveend', mapmoveend);
            map.removeEventListener('pointermove', mapPointerMove);
            map.getViewport().removeEventListener('mouseout', mouseoutMap);
            map.removeEventListener('click', onmapclick);
        };
    }, [map]);

    useEffect(() => map && moveMapTo(initExtent), [initExtent]);

    useEffect(() => {
        const done = loadSet.size === 0;
        setIsWorking(!done);
        if (verbose) console.log('@@@ update loadset:', loadSet.size, 'isWorking? ', !done);
        if (verbose) console.log('Inqueue', loadSet);
    }, [loadSet]);

    useEffect(() => {
        if (!map) return;
        map.getView().setZoom(zoom);
        setZoomRead(zoom);
        map.dispatchEvent('codemoveend');
    }, [zoom]);

    useEffect(() => {
        if (!map) return;
        map.getView().setCenter(center);
        map.dispatchEvent('codemoveend');
    }, [center]);

    useEffect(() => onLoadStatus(isWorking ? 0 : 1), [isWorking]);

    return (
        <>
            <Waiting visible={isWorking} />
            <div ref={mapRef} id={id} className="ol-map" role="application" aria-label="Interactive Map">
                {mappedChildren}
            </div>
        </>
    );
};
export default memo(Map);
